import { useEffect } from 'react';

/** SVG pattern ids used as Leaflet fillColor: url(#id) */
export const VACC_PATTERN_IDS = {
  /** Vaccination reported but coverage unknown */
  flagged: 'fast-vacc-hatch-flagged',
  /** RISP reported coverage — diagonal lines */
  band1: 'fast-vacc-hatch-1',
  band2: 'fast-vacc-hatch-2',
  band3: 'fast-vacc-hatch-3',
  band4: 'fast-vacc-hatch-4',
  /** FAST estimated coverage — dotted (FAOSTAT / doses÷pop) */
  estBand1: 'fast-vacc-est-1',
  estBand2: 'fast-vacc-est-2',
  estBand3: 'fast-vacc-est-3',
  estBand4: 'fast-vacc-est-4',
} as const;

const STROKE = '#0f766e';
const EST_STROKE = '#147a5c';
const FLAGGED_STROKE = '#b45309';
const FLAGGED_BG = 'rgba(180, 83, 9, 0.14)';
const DEFS_HOST_ID = 'fast-vacc-pattern-host';

const VaccinationPatternDefs: React.FC = () => {
  useEffect(() => {
    ensureDocumentPatterns();
  }, []);

  return null;
};

function ensureDocumentPatterns() {
  if (typeof document === 'undefined') return;
  const existing = document.getElementById(DEFS_HOST_ID);
  if (existing) existing.remove();

  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.id = DEFS_HOST_ID;
  svg.setAttribute('aria-hidden', 'true');
  svg.style.cssText =
    'position:absolute;width:0;height:0;overflow:hidden;pointer-events:none;';

  const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');

  {
    const pattern = document.createElementNS('http://www.w3.org/2000/svg', 'pattern');
    pattern.setAttribute('id', VACC_PATTERN_IDS.flagged);
    pattern.setAttribute('patternUnits', 'userSpaceOnUse');
    pattern.setAttribute('width', '10');
    pattern.setAttribute('height', '10');

    const bg = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    bg.setAttribute('width', '10');
    bg.setAttribute('height', '10');
    bg.setAttribute('fill', FLAGGED_BG);
    pattern.appendChild(bg);

    const dot = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    dot.setAttribute('cx', '5');
    dot.setAttribute('cy', '5');
    dot.setAttribute('r', '1.6');
    dot.setAttribute('fill', FLAGGED_STROKE);
    pattern.appendChild(dot);

    defs.appendChild(pattern);
  }

  const linePatterns: Array<{ id: string; size: number; strokeWidth: number }> = [
    { id: VACC_PATTERN_IDS.band1, size: 10, strokeWidth: 1 },
    { id: VACC_PATTERN_IDS.band2, size: 8, strokeWidth: 1.4 },
    { id: VACC_PATTERN_IDS.band3, size: 6, strokeWidth: 1.8 },
    { id: VACC_PATTERN_IDS.band4, size: 5, strokeWidth: 2.2 },
  ];

  for (const p of linePatterns) {
    const pattern = document.createElementNS('http://www.w3.org/2000/svg', 'pattern');
    pattern.setAttribute('id', p.id);
    pattern.setAttribute('patternUnits', 'userSpaceOnUse');
    pattern.setAttribute('width', String(p.size));
    pattern.setAttribute('height', String(p.size));
    pattern.setAttribute('patternTransform', 'rotate(45)');

    const bg = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    bg.setAttribute('width', String(p.size));
    bg.setAttribute('height', String(p.size));
    bg.setAttribute('fill', 'rgba(15, 118, 110, 0.12)');
    pattern.appendChild(bg);

    const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    line.setAttribute('x1', '0');
    line.setAttribute('y1', '0');
    line.setAttribute('x2', '0');
    line.setAttribute('y2', String(p.size));
    line.setAttribute('stroke', STROKE);
    line.setAttribute('stroke-width', String(p.strokeWidth));
    pattern.appendChild(line);

    defs.appendChild(pattern);
  }

  const estPatterns: Array<{ id: string; size: number; radius: number }> = [
    { id: VACC_PATTERN_IDS.estBand1, size: 12, radius: 1.2 },
    { id: VACC_PATTERN_IDS.estBand2, size: 10, radius: 1.5 },
    { id: VACC_PATTERN_IDS.estBand3, size: 8, radius: 1.8 },
    { id: VACC_PATTERN_IDS.estBand4, size: 6, radius: 2.1 },
  ];
  for (const p of estPatterns) {
    const pattern = document.createElementNS('http://www.w3.org/2000/svg', 'pattern');
    pattern.setAttribute('id', p.id);
    pattern.setAttribute('patternUnits', 'userSpaceOnUse');
    pattern.setAttribute('width', String(p.size));
    pattern.setAttribute('height', String(p.size));

    const bg = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    bg.setAttribute('width', String(p.size));
    bg.setAttribute('height', String(p.size));
    bg.setAttribute('fill', 'rgba(20, 122, 92, 0.10)');
    pattern.appendChild(bg);

    const dot = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    dot.setAttribute('cx', String(p.size / 2));
    dot.setAttribute('cy', String(p.size / 2));
    dot.setAttribute('r', String(p.radius));
    dot.setAttribute('fill', EST_STROKE);
    pattern.appendChild(dot);

    defs.appendChild(pattern);
  }

  svg.appendChild(defs);
  document.body.appendChild(svg);
}

export type VaccDoseBand = 1 | 2 | 3 | 4;
export type VaccFillStyle = 'risp' | 'estimated' | 'flagged';
export type VaccFillBand = 'flagged' | VaccDoseBand;

export function getVaccDoseBand(doses: number): VaccDoseBand | null {
  if (!doses || doses <= 0) return null;
  if (doses < 100_000) return 1;
  if (doses < 500_000) return 2;
  if (doses < 1_000_000) return 3;
  return 4;
}

export function getVaccCoverageBand(coveragePct: number): VaccDoseBand | null {
  if (!Number.isFinite(coveragePct) || coveragePct <= 0) return null;
  if (coveragePct < 25) return 1;
  if (coveragePct < 50) return 2;
  if (coveragePct < 75) return 3;
  return 4;
}

export function getVaccFillBand(doses: number, flagged: boolean): VaccFillBand | null {
  const doseBand = getVaccDoseBand(doses);
  if (doseBand) return doseBand;
  if (flagged) return 'flagged';
  return null;
}

export function getVaccFillBandFromCoverage(
  coveragePct: number | null | undefined,
  doses: number,
  flagged: boolean
): VaccFillBand | null {
  if (coveragePct != null && Number.isFinite(coveragePct) && coveragePct > 0) {
    return getVaccCoverageBand(coveragePct);
  }
  return getVaccFillBand(doses, flagged);
}

export function getVaccPatternUrl(
  band: VaccFillBand,
  style: VaccFillStyle = 'risp'
): string {
  if (band === 'flagged' || style === 'flagged') {
    return `url(#${VACC_PATTERN_IDS.flagged})`;
  }
  if (style === 'estimated') {
    const id =
      band === 1
        ? VACC_PATTERN_IDS.estBand1
        : band === 2
          ? VACC_PATTERN_IDS.estBand2
          : band === 3
            ? VACC_PATTERN_IDS.estBand3
            : VACC_PATTERN_IDS.estBand4;
    return `url(#${id})`;
  }
  const id =
    band === 1
      ? VACC_PATTERN_IDS.band1
      : band === 2
        ? VACC_PATTERN_IDS.band2
        : band === 3
          ? VACC_PATTERN_IDS.band3
          : VACC_PATTERN_IDS.band4;
  return `url(#${id})`;
}

export const VACC_FLAGGED_LABEL = 'Vaccinating (coverage unknown)';

export const VACC_DOSE_BAND_LABELS: Record<VaccDoseBand, string> = {
  1: '1 – 99,999 doses',
  2: '100,000 – 499,999',
  3: '500,000 – 999,999',
  4: '1,000,000+',
};

export const VACC_COVERAGE_BAND_LABELS: Record<VaccDoseBand, string> = {
  1: '1 – 24% coverage',
  2: '25 – 49% coverage',
  3: '50 – 74% coverage',
  4: '75 – 100% coverage',
};

export const VACC_ESTIMATED_NOTE =
  'Estimated by calculating doses / total population as per FAOSTAT modelled population';

export default VaccinationPatternDefs;
